import { useContext, useState } from "react";

import Box from "@/components/Box";
import { getAnswerColor } from "@/components/QuestionnaireReport";
import DiagramModal from "@/components/reports/DiagramModal";
import {
  PotentialSymptomsRow,
  SnpAccordionRow,
  FunctionalMarkerRow
} from "@/components/reports/InterpretationSharedRows";
import MarkersAssessedChart from "@/components/reports/MarkersAssessedChart";
import PrintFullReportButton from "@/components/reports/PrintFullReportButton";
import ReportInformationPanel from "@/components/reports/ReportInformationPanel";
import CardListContext from "@/contexts/CardListContext";
import Accordion from "@/tpo/Accordion";
import Stack from "@/tpo/Stack";
import { mapDnaDatapointToSnpRow } from "@/utils/getSnpColor";
import { getInterpretationColor } from "@/utils/shared";

export default function OverviewTab({ report, userReport, RelatedTestsSection, printUrl }) {
  const [activeDiagram, setActiveDiagram] = useState(null);

  const snpsForDiagram = userReport?.snpsForDiagram || {};
  const expectedSnps = userReport?.expectedSnps || {};

  const snpRows = (userReport?.dnaDatapointsOutOfRange || []).map(mapDnaDatapointToSnpRow);

  const ctx = useContext(CardListContext);

  const functionalRows = (userReport?.functionalDatapointsOutOfRange || []).map(datapoint => ({
    title: datapoint?.datapoint?.name,
    sampleType: datapoint?.sampleType,
    dateExtracted: datapoint?.dateExtracted,
    accentColor: getInterpretationColor(datapoint?.chartConfiguration?.interpretation),
    badgeText: datapoint?.value,
    units: datapoint?.datapoint?.units,
    onClick: () => {
      ctx?.viewCard({
        type: "datapoints",
        item: datapoint
      });
    }
  }));

  const symptomRows = [...(userReport?.questionsAndAnswersOutOfRange || [])]
    ?.sort((a, b) => b.answerScore - a.answerScore)
    .map(q => ({
      title: q.question.symptomGroup,
      risk: q.answerText || "No Answer",
      color: q.answerScore != null ? getAnswerColor(q.answerScore) : "midGrey",
      key: `symptom-${q.id}`
    }));

  return (
    <Stack gap={40}>
      <PrintFullReportButton printUrl={printUrl} centered />
      {report?.reportInformationPanels?.map(panel => (
        <ReportInformationPanel
          key={panel.id}
          {...panel}
          diagram={panel.diagram}
          onClick={() => setActiveDiagram({ diagramName: panel.diagram, title: panel.title })}
        />
      ))}
      <MarkersAssessedChart
        snpCounts={{
          inRange: userReport?.dnaDatapointCounts?.inRangeCount || 0,
          outOfRange: userReport?.dnaDatapointCounts?.outOfRangeCount || 0,
          untested: userReport?.dnaDatapointCounts?.untestedCount || 0
        }}
        functionalCounts={{
          inRange: userReport?.functionalDatapointCounts?.inRangeCount || 0,
          outOfRange: userReport?.functionalDatapointCounts?.outOfRangeCount || 0,
          untested: userReport?.functionalDatapointCounts?.untestedCount || 0
        }}
        symptomCounts={{
          inRange: userReport?.questionsAndAnswersCounts?.inRangeCount || 0,
          outOfRange: userReport?.questionsAndAnswersCounts?.outOfRangeCount || 0,
          untested: userReport?.questionsAndAnswersCounts?.untestedCount || 0
        }}
        sx={{ maxWidth: 720, width: "100%", mx: "auto" }}
      />
      {userReport?.dnaDatapointsOutOfRange?.length ||
      userReport?.functionalDatapointsOutOfRange?.length ||
      userReport?.questionsAndAnswersOutOfRange?.length ? (
        <Stack
          gap={[28, 28, 48]}
          bg="white"
          borderRadius={20}
          px={[24, 24, 40]}
          pt={[40, 40, 60]}
          pb={[60, 60, 80]}
          maxWidth={1024}
          mx="auto"
          width="100%"
        >
          <Stack gap={16}>
            <Box
              fontFamily="gilroyBold"
              fontSize={[32, 32, 44]}
              lineHeight={["36px", "36px", "50px"]}
            >
              Out-of-range markers
            </Box>
            <Box
              fontFamily="gilroyMedium"
              fontSize={[14, 14, 16]}
              lineHeight={["24px", "24px", "28px"]}
            >
              The following results are outside of the normal range and could indicate areas of
              focus. Visit the Interpretation tab to see all markers related to {report?.name}.
            </Box>
          </Stack>
          <Stack gap={16}>
            <Box
              fontFamily="gilroyBold"
              fontSize={[16, 16, 18]}
              lineHeight={["24px", "24px", "28px"]}
            >
              SNPS
            </Box>
            <Stack gap={0}>
              <Accordion size={0} variant="default">
                {snpRows.map((row, idx) => (
                  <SnpAccordionRow
                    key={`summary-snp-${row.badgeText}-${idx}`}
                    row={row}
                    isLast={idx === snpRows.length - 1}
                  />
                ))}
              </Accordion>
            </Stack>
          </Stack>
          <Stack gap={16}>
            <Box fontFamily="gilroyBold" fontSize={[18, 18, 20]} lineHeight="1.2">
              Functional markers
            </Box>
            <Stack gap={0}>
              {functionalRows.map((row, idx) => (
                <Box
                  key={`summary-functional-${idx}`}
                  sx={
                    idx === functionalRows.length - 1 ? { "& > div": { borderBottom: "none" } } : {}
                  }
                >
                  <FunctionalMarkerRow
                    title={row.title}
                    sampleType={row.sampleType}
                    dateExtracted={row.dateExtracted}
                    accentColor={row.accentColor}
                    badgeText={row.badgeText}
                    units={row.units}
                    onClick={row.onClick}
                  />
                </Box>
              ))}
            </Stack>
          </Stack>
          <Stack gap={0}>
            <Stack gap={8}>
              <Box fontFamily="gilroyBold" fontSize={[18, 18, 20]} lineHeight="1.2">
                Experienced Symptoms
              </Box>
              <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="28px" mt={"-8px"}>
                The following symptoms have been reported in the report symptomology questionnaire
              </Box>
            </Stack>
            <Box pt={16} />
            {symptomRows.map(row => (
              <PotentialSymptomsRow
                title={row.title}
                risk={row.risk}
                color={row.color}
                key={row.key}
              />
            ))}
          </Stack>
        </Stack>
      ) : null}
      {RelatedTestsSection ? <RelatedTestsSection report={report} /> : null}
      <DiagramModal
        show={Boolean(activeDiagram)}
        close={() => setActiveDiagram(null)}
        diagramName={activeDiagram?.diagramName}
        title={activeDiagram?.title}
        snpsForDiagram={snpsForDiagram}
        expectedSnps={expectedSnps}
      />
    </Stack>
  );
}
